import { useQuery } from "@tanstack/react-query";
import { format, subDays, subMonths } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, officeDataAvailable, isAllOffices, liveOffices, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import type {
  PgFicoGridRow,
  PolicyComplianceSummary,
  PolicyComplianceTierPoint,
  PrerequisiteGateMetric,
  RuleDefinition,
  RuleId,
  RuleMetric,
  SegmentFilter,
  SparkPoint,
} from "./types";

const MONTHS = 12;

function spark(points: number, start: number, end: number, jitter = 0.3): SparkPoint[] {
  return Array.from({ length: points }, (_, i) => {
    const t = points === 1 ? 1 : i / (points - 1);
    return {
      date: format(subDays(new Date(), points - 1 - i), "yyyy-MM-dd"),
      value: Number(Math.max(0, start + (end - start) * t + Math.sin(i * 1.7) * jitter).toFixed(2)),
    };
  });
}

function periods(count = MONTHS): string[] {
  return Array.from({ length: count }, (_, i) =>
    format(subMonths(new Date(), count - 1 - i), "MMM yy"),
  );
}

/** Rishabh's policy catalog — static reference content, identical across filters. */
export const RULE_DEFINITIONS: RuleDefinition[] = [
  {
    ruleId: "DQ1",
    ruleTier: "DQ",
    label: "Zero or blank deal amount",
    detectionLogic: "submit_amount = 0 on an approved deal",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "DQ2",
    ruleTier: "DQ",
    label: "Guarantor on file, no usable FICO",
    detectionLogic: "grntrs > 0 and fico_avg is blank, ≤0, <300, or >850",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote:
      "Reads fico_final rather than the fico_avg the rule text names: the two are identical wherever both are present, but fico_final is populated on every application.",
  },
  {
    ruleId: "A1",
    ruleTier: "A",
    label: "Approved with open bankruptcy",
    detectionLogic: "bankruptcy = 'B'",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "A2",
    ruleTier: "A",
    label: "Term over 84 months",
    detectionLogic: "original_term > 84 (exempt: cranes, up to 120)",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "partial_coverage",
    detectionNote:
      "The crane exemption has no source — no asset-level exemption list exists — so exempt cranes are currently counted as breaches. A sanity ceiling also excludes terms above 360 months, which are data errors rather than policy breaches.",
  },
  {
    ruleId: "A3",
    ruleTier: "A",
    label: "Booked more than 10% over approval",
    detectionLogic: "booking_amount > 1.10 × approval_amount",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "A4",
    ruleTier: "A",
    label: "Funded on a stale approval",
    detectionLogic: "booking_date − last_approval_date > 90 days",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "A5",
    ruleTier: "A",
    label: "Approved with no risk rating",
    detectionLogic: "risk_rating_desc is null",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "B1a",
    ruleTier: "B",
    label: "PG deal below FICO floor",
    detectionLogic:
      "PG='Y' and fico_avg is below the grid floor for this grade and time-in-business — see PG FICO grid",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "partial_coverage",
    detectionNote:
      "Undeterminable for the ~37% of applications with no time-in-business, and for grades with no grid row. The count is a floor, not a total.",
  },
  {
    ruleId: "B1b",
    ruleTier: "B",
    label: "Grade not attainable at tenure",
    detectionLogic: "grade ∈ {A1, B1} and time-in-business < 2 years",
    isReviewFlag: true,
    specialNote:
      "Many of these are legitimate PASS-score approvals — the engine auto-rates sub-$100k deals as A1 regardless of tenure. Reconcile against PASS-score approvals before treating as an exception.",
    detectionStatus: "partial_coverage",
    detectionNote:
      "Undeterminable on the same ~37% with no tenure. Open question for Credit: the A-family variants 'A - PREFERRED' and 'A - ENHANCED' are currently excluded, since the rule names A1 specifically.",
  },
  {
    ruleId: "B2",
    ruleTier: "B",
    label: "Corp-only deal that should have required a PG",
    detectionLogic:
      "PG='N' and both high_credit and paynet_high_credit are below submit_amount",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "partial_coverage",
    detectionNote:
      "One of the two required inputs is missing: paynet_high_credit has no column anywhere, so only the high_credit leg is tested. This makes the count an UPPER bound — some of these would clear on the PayNet leg.",
  },
  {
    ruleId: "B4",
    ruleTier: "B",
    label: "C-grade kept on book",
    detectionLogic: "risk_rating_desc = 'C' on a booked deal",
    isReviewFlag: false,
    specialNote:
      "No manual recourse review needed — recourse-backed deals are already relabeled 'A - Enhanced' in this field, so any deal still showing 'C' has no recourse by construction.",
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "C1",
    ruleTier: "C",
    label: "Borrower aggregates past app-only tier",
    detectionLogic: "sum of submit_amount for this borrower's group exceeds $250,000",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote: null,
  },
  {
    ruleId: "C2",
    ruleTier: "C",
    label: "Approved with liens, judgments, or suits",
    detectionLogic: "material liens, judgments, or suits on file",
    isReviewFlag: false,
    specialNote: null,
    detectionStatus: "implemented",
    detectionNote:
      "\"Material\" is not encoded anywhere, so every flagged lien, suit or judgement counts. This may overstate against Credit's intent.",
  },
];

export const PG_FICO_GRID: PgFicoGridRow[] = [
  {
    riskGrade: "A / A1",
    under2Years: "NA → B1b",
    twoToFourYears: "700",
    fourToFiveYears: "680",
    overFiveYears: "660",
  },
  {
    riskGrade: "B1",
    under2Years: "NA → B1b",
    twoToFourYears: "680",
    fourToFiveYears: "660",
    overFiveYears: "650",
  },
  {
    riskGrade: "B2 / B3",
    under2Years: "700",
    twoToFourYears: "660",
    fourToFiveYears: "650",
    overFiveYears: "640",
  },
];

export const RULE_TIER_ORDER: RuleId[] = [
  "A1",
  "A2",
  "A3",
  "A4",
  "A5",
  "B1a",
  "B1b",
  "B2",
  "B4",
  "C1",
  "C2",
];

/** Segment weighting: the PG book carries more grid exceptions than Corp. */
function segmentWeight(segment: SegmentFilter) {
  if (segment === "PG") return 1.24;
  if (segment === "Corp") return 0.74;
  return 1;
}

/**
 * Base current-period shape per rule.
 * Tier A stays rare by policy design; Tier B dominates (B1b highest of all,
 * mostly unreconciled); Tier C sits between the two.
 */
const RULE_BASE: Record<
  Exclude<RuleId, "DQ1" | "DQ2">,
  { count: number; candidateShare: number; trendStart: number }
> = {
  A1: { count: 4, candidateShare: 0.5, trendStart: 1.4 },
  A2: { count: 9, candidateShare: 0.44, trendStart: 1.2 },
  A3: { count: 14, candidateShare: 0.5, trendStart: 1.15 },
  A4: { count: 11, candidateShare: 0.46, trendStart: 1.3 },
  A5: { count: 6, candidateShare: 0.55, trendStart: 1.6 },
  B1a: { count: 96, candidateShare: 0.52, trendStart: 1.1 },
  B1b: { count: 418, candidateShare: 0.86, trendStart: 0.82 },
  B2: { count: 74, candidateShare: 0.48, trendStart: 1.05 },
  B4: { count: 21, candidateShare: 0.04, trendStart: 1.1 },
  C1: { count: 52, candidateShare: 0.6, trendStart: 0.95 },
  C2: { count: 38, candidateShare: 0.58, trendStart: 1.2 },
};

/** PG/Corp tilt — B2 is Corp-only by construction, B1a/B1b are PG-driven. */
const RULE_SEGMENT_TILT: Partial<Record<RuleId, { PG: number; Corp: number }>> = {
  B1a: { PG: 1.62, Corp: 0.12 },
  B1b: { PG: 1.34, Corp: 0.5 },
  B2: { PG: 0.05, Corp: 1.85 },
  C1: { PG: 1.12, Corp: 0.88 },
};

const APPROVED_POPULATION = 3120;

function buildRuleMetrics(segment: SegmentFilter): RuleMetric[] {
  const w = segmentWeight(segment);
  const approved = Math.round(APPROVED_POPULATION * (segment === "all" ? 1 : 0.55));

  return RULE_TIER_ORDER.map((ruleId) => {
    const base = RULE_BASE[ruleId as Exclude<RuleId, "DQ1" | "DQ2">];
    const tilt = segment === "all" ? 1 : (RULE_SEGMENT_TILT[ruleId]?.[segment] ?? 1);
    const count = Math.max(1, Math.round(base.count * w * tilt));
    const candidateCount = Math.round(count * base.candidateShare);

    return {
      ruleId,
      count,
      pctOfApprovedPopulation: Number(((count / approved) * 100).toFixed(2)),
      candidateCount,
      confirmedCount: count - candidateCount,
      sparkline: spark(12, Math.max(1, count * base.trendStart), count, count * 0.05),
    };
  });
}

function buildGates(segment: SegmentFilter): PrerequisiteGateMetric[] {
  const w = segmentWeight(segment);
  const total = Math.round(APPROVED_POPULATION * (segment === "all" ? 1 : 0.55));
  const dq1 = Math.round(23 * w);
  const dq2 = Math.round(61 * w);

  return [
    {
      ruleId: "DQ1",
      excludedCount: dq1,
      excludedPctOfTotal: Number(((dq1 / total) * 100).toFixed(2)),
    },
    {
      ruleId: "DQ2",
      excludedCount: dq2,
      excludedPctOfTotal: Number(((dq2 / total) * 100).toFixed(2)),
    },
  ];
}

function buildComplianceTrend(segment: SegmentFilter): PolicyComplianceTierPoint[] {
  // PG runs a couple of points below Corp on every grade.
  const offset = segment === "PG" ? -1.6 : segment === "Corp" ? 1.1 : 0;

  return periods().map((period, i) => {
    const t = i / (MONTHS - 1);
    return {
      period,
      complianceRateA: Number((96.8 + offset * 0.4 + Math.sin(i * 1.5) * 0.4).toFixed(1)),
      complianceRateB1: Number((93.1 + offset * 0.7 + Math.cos(i * 1.3) * 0.6).toFixed(1)),
      complianceRateB2: Number((89.4 + offset + Math.sin(i * 1.1) * 0.7).toFixed(1)),
      // B3 drifts down across the period — the tier-drift signal.
      complianceRateB3: Number((87.6 - 5.4 * t + offset + Math.cos(i * 1.7) * 0.6).toFixed(1)),
      // Zero in the mock: every invented application has a tenure and a grade, so nothing is
      // undeterminable. The live endpoint reports a real figure here -- roughly 37% of
      // applications carry no time-in-business, which makes rules B1a and B1b unanswerable for
      // them rather than passing.
      undeterminableCount: 0,
    };
  });
}

function emptySummary(): PolicyComplianceSummary {
  return {
    dataAvailable: false,
    gates: [],
    complianceTrend: [],
    ruleDefinitions: RULE_DEFINITIONS,
    ruleMetrics: [],
    pgFicoGrid: PG_FICO_GRID,
  };
}

function generateSummary(
  offices: OfficeMapping[],
  params: { office?: string; segment: SegmentFilter },
): PolicyComplianceSummary {
  const office = params.office ?? "all";
  const dataAvailable = officeDataAvailable(offices, office);

  if (!dataAvailable) return emptySummary();

  return {
    dataAvailable: true,
    gates: buildGates(params.segment),
    complianceTrend: buildComplianceTrend(params.segment),
    ruleDefinitions: RULE_DEFINITIONS,
    ruleMetrics: buildRuleMetrics(params.segment),
    pgFicoGrid: PG_FICO_GRID,
  };
}

export async function fetchPolicyComplianceSummary(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean; segment: SegmentFilter },
) {
  if (mockFor("/policy-compliance/summary")) return mockResponse(generateSummary(offices, params));

  return apiGet<PolicyComplianceSummary>("/policy-compliance/summary", {
    range: params.range,
    office: params.office,
    segment: params.segment,
    compare: params.compare ? "true" : undefined,
  });
}

export function usePolicyComplianceSummary(
  segment: SegmentFilter,
  params: { range?: string; office?: string; compare?: boolean } = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("policy-compliance-summary", {
      ...params,
      segment,
      offices: officesFingerprint(offices),
    }),
    queryFn: () => fetchPolicyComplianceSummary(offices ?? [], { ...params, segment }),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}

/**
 * Single source of truth for rule reference data — other pages (Deal-Level
 * Exceptions Log) import these types and RULE_DEFINITIONS from here rather
 * than keeping a second copy.
 */
export type { RuleDefinition, RuleId, RuleTier } from "./types";

/**
 * The same static catalog under the camelCase name other pages import.
 * Single source of truth — Manage Alerts and the Exceptions Log both read
 * this array rather than redefining the rules.
 */
export const ruleDefinitions = RULE_DEFINITIONS;
